import React, { useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import { Table, Button, Modal, message, Row, Col, Dropdown } from 'antd';
import { DownloadOutlined, DownOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import DateRangeFilterItem from '../../../../delivery/components/DateRangeFilterItem';
import InputFilterItem from '../../../../delivery/components/InputFilterItem';
import { formatTimeRangeToISO } from '../../../../../utils/dateTimeUtils';
import { resolvePricingSchemeMessage } from '../../../../../utils/graphqlUtils';
import { QUERY_PRICING_SCHEMES } from 'graphql/queries/settlement/getPricingSchemeList';
import { DELETE_PRICING_SCHEME } from 'graphql/mutations/settlement/deletePricingScheme';
import {
  CREATE_PRICING_SCHEME_DOWNLOAD_TASK,
  type CreatePricingSchemeDownloadTaskResponse,
  type CreatePricingSchemeDownloadTaskVariables,
} from 'graphql/mutations/settlement/createPricingSchemeDownloadTask';
import ParcelsDownloadModal from '../../../parcels/components/ParcelsDownloadModal';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import { BUSINESS_SCENARIO_COST_PERIODIC } from 'graphql/queries/settlement/getSettlementCommonQueries';
import { formatFixedUtcDateTime } from 'utils/timezoneFormatter';

interface SchemeItem {
  id: number;
  pricingSchemeName: string;
  pricingSchemeDesc?: string | null;
  bindCount?: number | null;
  creator?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface Props {
  businessScenario: number | null;
  onCreateClick: () => void;
  onEditClick: (id: number) => void;
  onViewClick: (id: number) => void;
  refreshKey?: number;
}

const DEFAULT_HALF_YEAR_RANGE: [string, string] = [
  dayjs().subtract(6, 'month').format('YYYY-MM-DD'),
  dayjs().format('YYYY-MM-DD'),
];

const CostPeriodicPricingList: React.FC<Props> = ({ businessScenario, onCreateClick, onEditClick, onViewClick, refreshKey }) => {
  const { t } = useTranslation();

  const [createdAtRange, setCreatedAtRange] = useState<[string | null, string | null] | null>(DEFAULT_HALF_YEAR_RANGE);
  const [creatorName, setCreatorName] = useState<string | undefined>(undefined);
  const [pricingSchemeId, setPricingSchemeId] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [activeFilters, setActiveFilters] = useState({
    createdAt: DEFAULT_HALF_YEAR_RANGE as [string | null, string | null] | null,
    creatorName: undefined as string | undefined,
    pricingSchemeId: undefined as string | undefined,
  });
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [generatedFileName, setGeneratedFileName] = useState('');

  const queryVariables = useMemo(() => {
    const vars: Record<string, unknown> = {
      businessScenario: BUSINESS_SCENARIO_COST_PERIODIC,
      page: currentPage,
      size: pageSize,
    };
    if (activeFilters.pricingSchemeId) vars.pricingSchemeId = parseInt(activeFilters.pricingSchemeId, 10);
    if (activeFilters.creatorName) vars.creatorName = activeFilters.creatorName;
    if (activeFilters.createdAt?.[0] && activeFilters.createdAt?.[1]) {
      const range = formatTimeRangeToISO(activeFilters.createdAt);
      if (range?.start && range?.end) { vars.createdAtStart = range.start; vars.createdAtEnd = range.end; }
    }
    return vars;
  }, [activeFilters, currentPage, pageSize]);

  const { data, loading, refetch } = useQuery(QUERY_PRICING_SCHEMES, {
    variables: queryVariables,
    skip: !businessScenario,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (refreshKey && refreshKey > 0) refetch();
  }, [refreshKey]);

  const [deleteScheme, { loading: deleting }] = useMutation(DELETE_PRICING_SCHEME, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const [createDownloadTask, { loading: exportLoading }] = useMutation<
    CreatePricingSchemeDownloadTaskResponse,
    CreatePricingSchemeDownloadTaskVariables
  >(CREATE_PRICING_SCHEME_DOWNLOAD_TASK);

  const handleExport = async (type: 'csv' | 'excel' | 'json') => {
    const totalElements = data?.queryPricingSchemes?.totalElements || 0;
    if (totalElements === 0) { message.warning('没有数据可导出'); return; }
    try {
      const exportVars: CreatePricingSchemeDownloadTaskVariables = {
        format: type,
        businessScenario: BUSINESS_SCENARIO_COST_PERIODIC,
      };
      if (activeFilters.pricingSchemeId) exportVars.pricingSchemeId = parseInt(activeFilters.pricingSchemeId, 10);
      if (activeFilters.creatorName) exportVars.creatorName = activeFilters.creatorName;
      if (activeFilters.createdAt?.[0] && activeFilters.createdAt?.[1]) {
        const range = formatTimeRangeToISO(activeFilters.createdAt);
        if (range?.start && range?.end) { exportVars.createdAtStart = range.start; exportVars.createdAtEnd = range.end; }
      }
      const { data: res } = await createDownloadTask({ variables: exportVars, fetchPolicy: 'network-only' });
      const exportResult = res?.createPricingSchemeDownloadTask;
      if (!exportResult?.data) { message.error(resolvePricingSchemeMessage(exportResult, '提交下载中心失败，请重试')); return; }
      setGeneratedFileName(exportResult.data);
      setDownloadModalVisible(true);
    } catch {
      message.error('提交下载中心失败，请重试');
    }
  };

  const exportMenuItems: MenuProps['items'] = [
    { key: 'csv',   label: t('settlement.priceCenter.actions.export') + ' CSV',   icon: <DownloadOutlined />, onClick: () => handleExport('csv') },
    { key: 'excel', label: t('settlement.priceCenter.actions.export') + ' Excel', icon: <DownloadOutlined />, onClick: () => handleExport('excel') },
    { key: 'json',  label: t('settlement.priceCenter.actions.export') + ' JSON',  icon: <DownloadOutlined />, onClick: () => handleExport('json') },
  ];

  const handleDelete = (schemeId: number, schemeName: string) => {
    Modal.confirm({
      title: <span style={{ color: '#ff4d4f' }}>确认删除</span>,
      content: `确定要删除方案"${schemeName}"吗？`,
      okText: '确认',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        const { data: res, errors } = await deleteScheme({ variables: { pricingSchemeId: schemeId } });
        if (errors?.length) { message.error(errors[0].message); return; }
        const result = res?.deletePricingScheme;
        if (result?.success) { message.success(t('settlement.priceCenter.messages.deleteSuccess')); handleFilter(); }
        else message.error(resolvePricingSchemeMessage(result, t('settlement.priceCenter.messages.deleteFailed')));
      },
    });
  };

  const handleFilter = () => {
    setActiveFilters({ createdAt: createdAtRange, creatorName, pricingSchemeId });
    setCurrentPage(1);
    const vars: Record<string, unknown> = {
      businessScenario: BUSINESS_SCENARIO_COST_PERIODIC,
      page: 1,
      size: pageSize,
    };
    if (pricingSchemeId) vars.pricingSchemeId = parseInt(pricingSchemeId, 10);
    if (creatorName) vars.creatorName = creatorName;
    if (createdAtRange?.[0] && createdAtRange?.[1]) {
      const range = formatTimeRangeToISO(createdAtRange);
      if (range?.start && range?.end) { vars.createdAtStart = range.start; vars.createdAtEnd = range.end; }
    }
    refetch(vars);
  };

  const handleReset = () => {
    setCreatedAtRange(DEFAULT_HALF_YEAR_RANGE);
    setCreatorName(undefined);
    setPricingSchemeId(undefined);
    setActiveFilters({ createdAt: DEFAULT_HALF_YEAR_RANGE, creatorName: undefined, pricingSchemeId: undefined });
    setCurrentPage(1);
  };

  const columns: ColumnsType<SchemeItem> = [
    { title: '方案ID', dataIndex: 'id', width: 100 },
    { title: '方案名称', dataIndex: 'pricingSchemeName', width: 200 },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 180,
      render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{formatFixedUtcDateTime(text)}</div>,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      width: 180,
      render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{formatFixedUtcDateTime(text)}</div>,
    },
    {
      title: '绑定数量',
      dataIndex: 'bindCount',
      width: 120,
      render: v => (v != null ? v : '-'),
    },
    { title: '创建人', dataIndex: 'creator', width: 120, render: v => v ?? '-' },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <>
          <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_PLAN_CHECK]}>
            <Button type="link" size="small" onClick={() => onViewClick(record.id)}>{t('settlement.priceCenter.actions.view')}</Button>
          </HasResource>
          <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_PLAN_OPERATE]}>
            <Button type="link" size="small" onClick={() => onEditClick(record.id)}>{t('settlement.priceCenter.actions.edit')}</Button>
            <Button
              type="link"
              size="small"
              danger
              onClick={() => handleDelete(record.id, record.pricingSchemeName)}
              disabled={deleting}
            >
              {t('settlement.priceCenter.actions.delete')}
            </Button>
          </HasResource>
        </>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 18]} wrap>
        <Col xs={24} sm={24} md={24} lg={12} xl={10}>
          <DateRangeFilterItem
            label="方案创建时间"
            spanClassName="w-[9em]"
            value={createdAtRange}
            onChange={setCreatedAtRange}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
          <InputFilterItem
            label="创建人"
            spanClassName="w-[9em]"
            value={creatorName}
            onChange={e => setCreatorName(e.target.value || undefined)}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
          <InputFilterItem
            label="方案ID"
            spanClassName="w-[9em]"
            value={pricingSchemeId}
            onChange={e => setPricingSchemeId(e.target.value || undefined)}
          />
        </Col>
        <Col span={24}>
          <Row justify="end">
            <Col>
              <div className="flex gap-4">
                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_PLAN_OPERATE]}>
                  <Button type="primary" onClick={onCreateClick} disabled={!businessScenario}>
                    {t('settlement.priceCenter.actions.newPlan')}
                  </Button>
                </HasResource>
                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_PLAN_CHECK]}>
                  <Button type="primary" onClick={handleFilter}>{t('settlement.priceCenter.actions.filter')}</Button>
                </HasResource>
                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_PLAN_OPERATE]}>
                  <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight" trigger={['click']}>
                    <Button type="primary" icon={<DownloadOutlined />} loading={exportLoading}>
                      {t('settlement.priceCenter.actions.export')} <DownOutlined />
                    </Button>
                  </Dropdown>
                </HasResource>
                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_PLAN_CHECK]}>
                  <Button onClick={handleReset}>{t('settlement.priceCenter.actions.reset')}</Button>
                </HasResource>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="mt-6">
        <Table
          columns={columns}
          dataSource={data?.queryPricingSchemes?.items || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize,
            total: data?.queryPricingSchemes?.totalElements || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: total => t('table.pagination.totalItems', { total }),
            onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
            onShowSizeChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
          }}
        />
      </div>
      <ParcelsDownloadModal
        visible={downloadModalVisible}
        onClose={() => setDownloadModalVisible(false)}
        fileName={generatedFileName}
      />
    </div>
  );
};

export default CostPeriodicPricingList;
